import { PayloadAction } from "@reduxjs/toolkit";
import { AnyAction } from "redux";

import {
  IJSONAPIItem,
  FulfilledAction,
  IActionStates,
  PendingAction,
  RejectedAction,
  IDataKeys,
} from "shared/types";
import { BAND_ACTIONS, BAND_ACTION_PREFIX, USER_ACTIONS } from "Bands/types";

const matchPrefix = (action: AnyAction): boolean => action.type.startsWith(BAND_ACTION_PREFIX);

// These actions are not be handled by the matchers for the fulfilled state
const isFulfilledActionExceptionType = (action: AnyAction): boolean =>
  isDeleteFulfilledActionType(action) ||
  isCreatedFulfilledActionType(action) ||
  isReplaceFulfilledActionType(action) ||
  isEditFulfilledActionType(action) ||
  isBandDetailFulfilledActionType(action) ||
  action.type.startsWith(BAND_ACTIONS.getEvents) ||
  action.type.startsWith(BAND_ACTIONS.getEventGuests) ||
  action.type.startsWith(BAND_ACTIONS.downloadEventGuestlistBlock);

// These actions are not handled by the general matcher for the fulfilled state
// but by the created fulfilled matcher that passes the id of the created item to the meta
const isCreatedFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(BAND_ACTIONS.createBand) ||
  action.type.startsWith(BAND_ACTIONS.createEvent) ||
  action.type.startsWith(BAND_ACTIONS.createBand) ||
  action.type.startsWith(BAND_ACTIONS.createBandSubscription) ||
  action.type.startsWith(BAND_ACTIONS.editBandSubscription) ||
  action.type.startsWith(BAND_ACTIONS.editSubscriptionPayment) ||
  action.type.startsWith(BAND_ACTIONS.createCustomSubscription) ||
  action.type.startsWith(BAND_ACTIONS.requestCustomSubscription) ||
  action.type.startsWith(BAND_ACTIONS.createBandInvitation) ||
  action.type.startsWith(BAND_ACTIONS.createEventPeopleBlock) ||
  action.type.startsWith(BAND_ACTIONS.addMembersToPeopleBlock) ||
  action.type.startsWith(BAND_ACTIONS.createEventScheduleBlock) ||
  action.type.startsWith(BAND_ACTIONS.createEventInfoBlock) ||
  action.type.startsWith(BAND_ACTIONS.createInfoBlockItem) ||
  action.type.startsWith(BAND_ACTIONS.createEventVenueBlock) ||
  action.type.startsWith(BAND_ACTIONS.createEventGuestlistBlock) ||
  action.type.startsWith(BAND_ACTIONS.createEventTicketAndPass) ||
  action.type.startsWith(BAND_ACTIONS.createEventGuest) ||
  action.type.startsWith(BAND_ACTIONS.createEventTravelBlock) ||
  action.type.startsWith(BAND_ACTIONS.createTravelLeg) ||
  action.type.startsWith(BAND_ACTIONS.createTravelBlockPassenger) ||
  action.type.startsWith(BAND_ACTIONS.createAccommodationBlock) ||
  action.type.startsWith(BAND_ACTIONS.createAccommodationRoomType) ||
  action.type.startsWith(BAND_ACTIONS.createEventDetailsExport) ||
  action.type.startsWith(BAND_ACTIONS.getInvoice) || // return invoice id with create action
  action.type.startsWith(USER_ACTIONS.createUserAddress) ||
  action.type.startsWith(USER_ACTIONS.createUserPhoneNumber) ||
  action.type.startsWith(USER_ACTIONS.createUserDrivingPermit) ||
  action.type.startsWith(USER_ACTIONS.createUserEmergencyContact) ||
  action.type.startsWith(USER_ACTIONS.createUserHomeBase) ||
  action.type.startsWith(USER_ACTIONS.createUserLoyaltyProgram) ||
  action.type.startsWith(USER_ACTIONS.createUserPassport) ||
  action.type.startsWith(USER_ACTIONS.createUserSocialSecurityForm) ||
  action.type.startsWith(USER_ACTIONS.createUserTravelVisa) ||
  action.type.startsWith(USER_ACTIONS.createUserVehicle) ||
  action.type.startsWith(BAND_ACTIONS.emailEventDetailsExport) ||
  action.type.startsWith(BAND_ACTIONS.copyEventBlocks);
// These actions are not handled by the general matcher for the fulfilled state
// but by the deleted fulfilled matcher that removes the item from the state
const isDeleteFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(BAND_ACTIONS.deleteBand) ||
  action.type.startsWith(BAND_ACTIONS.deleteEvent) ||
  action.type.startsWith(BAND_ACTIONS.deleteBandInvitation) ||
  action.type.startsWith(BAND_ACTIONS.deleteEventPeopleBlock) ||
  action.type.startsWith(BAND_ACTIONS.deleteEventScheduleBlock) ||
  action.type.startsWith(BAND_ACTIONS.deleteEventInfoBlock) ||
  action.type.startsWith(BAND_ACTIONS.deleteEventVenueBlock) ||
  action.type.startsWith(BAND_ACTIONS.deleteInfoBlockItem) ||
  action.type.startsWith(BAND_ACTIONS.deleteEventGuestlistBlock) ||
  action.type.startsWith(BAND_ACTIONS.deleteEventTicketAndPass) ||
  action.type.startsWith(BAND_ACTIONS.deleteEventGuest) ||
  action.type.startsWith(BAND_ACTIONS.deleteEventTravelBlock) ||
  action.type.startsWith(BAND_ACTIONS.deleteTravelLeg) ||
  action.type.startsWith(BAND_ACTIONS.deleteTravelBlockPassenger) ||
  action.type.startsWith(BAND_ACTIONS.deleteCustomSubscriptionRequest) ||
  action.type.startsWith(BAND_ACTIONS.deleteAccommodationBlock) ||
  action.type.startsWith(BAND_ACTIONS.deleteAccommodationRoomType) ||
  action.type.startsWith(BAND_ACTIONS.deleteAccommodationReservation) ||
  action.type.startsWith(USER_ACTIONS.deleteUserAddress) ||
  action.type.startsWith(USER_ACTIONS.deleteUserPhoneNumber) ||
  action.type.startsWith(USER_ACTIONS.deleteUserDrivingPermit) ||
  action.type.startsWith(USER_ACTIONS.deleteUserEmergencyContact) ||
  action.type.startsWith(USER_ACTIONS.deleteUserHomeBase) ||
  action.type.startsWith(USER_ACTIONS.deleteUserLoyaltyProgram) ||
  action.type.startsWith(USER_ACTIONS.deleteUserPassport) ||
  action.type.startsWith(USER_ACTIONS.deleteUserSocialSecurityForm) ||
  action.type.startsWith(USER_ACTIONS.deleteUserTravelVisa) ||
  action.type.startsWith(USER_ACTIONS.deleteUserVehicle);

// These actions are not handled by the general matcher for the fulfilled state
// but by the edit fulfilled matcher that replaces the item in the state
const isEditFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(BAND_ACTIONS.editBand) ||
  action.type.startsWith(BAND_ACTIONS.editEvent) ||
  action.type.startsWith(BAND_ACTIONS.editCompany) ||
  action.type.startsWith(BAND_ACTIONS.editBandInvitation) ||
  action.type.startsWith(BAND_ACTIONS.editEventInfoBlock) ||
  action.type.startsWith(BAND_ACTIONS.editInfoBlockItem) ||
  action.type.startsWith(BAND_ACTIONS.editUserPermissions) ||
  action.type.startsWith(BAND_ACTIONS.editEventGuestlistBlock) ||
  action.type.startsWith(BAND_ACTIONS.editEventTicketAndPass) ||
  action.type.startsWith(BAND_ACTIONS.editEventGuest) ||
  action.type.startsWith(BAND_ACTIONS.editEventTravelBlock) ||
  action.type.startsWith(BAND_ACTIONS.editTravelLeg) ||
  action.type.startsWith(BAND_ACTIONS.cancelBandSubscription) ||
  action.type.startsWith(USER_ACTIONS.editUser) ||
  action.type.startsWith(USER_ACTIONS.editUserAddress) ||
  action.type.startsWith(USER_ACTIONS.editUserPhoneNumber) ||
  action.type.startsWith(USER_ACTIONS.editUserDrivingPermit) ||
  action.type.startsWith(USER_ACTIONS.editUserEmergencyContact) ||
  action.type.startsWith(USER_ACTIONS.editUserHomeBase) ||
  action.type.startsWith(USER_ACTIONS.editUserLoyaltyProgram) ||
  action.type.startsWith(USER_ACTIONS.editUserPassport) ||
  action.type.startsWith(USER_ACTIONS.editUserSocialSecurityForm) ||
  action.type.startsWith(USER_ACTIONS.editUserTravelVisa) ||
  action.type.startsWith(USER_ACTIONS.editUserVehicle) ||
  action.type.startsWith(BAND_ACTIONS.editBandIcal) ||
  action.type.startsWith(BAND_ACTIONS.editAccommodationBlock) ||
  action.type.startsWith(BAND_ACTIONS.editAccommodationRoomType) ||
  action.type.startsWith(BAND_ACTIONS.editAccommodationReservation);

// These actions are not handled by the general matcher for the fulfilled state
// but by the replace fulfilled matcher that replaces the data in the state instead of merging it
const isReplaceFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(BAND_ACTIONS.getBand) ||
  action.type.startsWith(BAND_ACTIONS.getUserBands) ||
  action.type.startsWith(BAND_ACTIONS.getMyBandUsers) ||
  action.type.startsWith(BAND_ACTIONS.getAdminBands) ||
  action.type.startsWith(BAND_ACTIONS.getUserCompanies) ||
  action.type.startsWith(BAND_ACTIONS.getCustomSubscriptionRequests) ||
  action.type.startsWith(BAND_ACTIONS.getCompanySubscriptions) ||
  action.type.startsWith(BAND_ACTIONS.getCompanyInvoices) ||
  action.type.startsWith(BAND_ACTIONS.getPaymentInfo) ||
  action.type.startsWith(BAND_ACTIONS.getPendingBandInvitations) ||
  action.type.startsWith(BAND_ACTIONS.getSentBandInvitations) ||
  action.type.startsWith(BAND_ACTIONS.getBandUsers) ||
  action.type.startsWith(BAND_ACTIONS.getEventPeopleBlocks) ||
  action.type.startsWith(BAND_ACTIONS.getEventScheduleBlock) ||
  action.type.startsWith(BAND_ACTIONS.getEventInfoBlocks) ||
  action.type.startsWith(BAND_ACTIONS.getEventVenueBlocks) ||
  action.type.startsWith(BAND_ACTIONS.getEventGuestlistBlocks) ||
  action.type.startsWith(BAND_ACTIONS.getEventTravelBlocks) ||
  action.type.startsWith(BAND_ACTIONS.getEventAccommodationBlocks) ||
  action.type.startsWith(BAND_ACTIONS.getEventSuggestedLocations) ||
  action.type.startsWith(BAND_ACTIONS.getEventGoogleLocations) ||
  action.type.startsWith(BAND_ACTIONS.getPermissionTypes) ||
  action.type.startsWith(USER_ACTIONS.getUserAddresses) ||
  action.type.startsWith(USER_ACTIONS.getUserPhoneNumbers) ||
  action.type.startsWith(USER_ACTIONS.getUserDrivingPermits) ||
  action.type.startsWith(USER_ACTIONS.getUserEmergencyContacts) ||
  action.type.startsWith(USER_ACTIONS.getUserHomeBases) ||
  action.type.startsWith(USER_ACTIONS.getUserLoyaltyPrograms) ||
  action.type.startsWith(USER_ACTIONS.getUserPassports) ||
  action.type.startsWith(USER_ACTIONS.getUserSocialSecurityForms) ||
  action.type.startsWith(USER_ACTIONS.getUserTravelVisas) ||
  action.type.startsWith(USER_ACTIONS.getUserVehicles) ||
  action.type.startsWith(BAND_ACTIONS.getBandIcal) ||
  action.type.startsWith(BAND_ACTIONS.getEventDetailsExport) ||
  action.type.startsWith(BAND_ACTIONS.getLastEventDetailsExportSent) ||
  action.type.startsWith(BAND_ACTIONS.getLastGuestlistSent) ||
  action.type.startsWith(BAND_ACTIONS.fetchPrefillFlight);

const isBandDetailFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(BAND_ACTIONS.getBand) ||
  action.type.startsWith(BAND_ACTIONS.editEventTypes) ||
  action.type.startsWith(BAND_ACTIONS.editInfoBlockTypes) ||
  action.type.startsWith(BAND_ACTIONS.editTicketAndPassTypes) ||
  action.type.startsWith(BAND_ACTIONS.editMemberCategories);

// These actions are handled by the general matcher for the fulfilled state
// but also by the info block detail fulfilled matcher that adds its linked
// contacts, files, places and images to the redux store under the right key
const isInfoBlockDetailFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(BAND_ACTIONS.getEventInfoBlocks) ||
  action.type.startsWith(BAND_ACTIONS.getEventInfoBlock) ||
  action.type.startsWith(BAND_ACTIONS.editEventInfoBlock);

const isPaginatedFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(BAND_ACTIONS.getAdminBands) ||
  action.type.startsWith(BAND_ACTIONS.getCompanyInvoices) ||
  action.type.startsWith(BAND_ACTIONS.getPendingBandInvitations) ||
  action.type.startsWith(BAND_ACTIONS.getSentBandInvitations) ||
  action.type.startsWith(BAND_ACTIONS.getBandUsers) ||
  action.type.startsWith(BAND_ACTIONS.getInfoBlockItems) ||
  action.type.startsWith(BAND_ACTIONS.getTravelBlockPassengers) ||
  action.type.startsWith(BAND_ACTIONS.getBandUsersPermissions) ||
  action.type.startsWith(BAND_ACTIONS.getEventGuests) ||
  action.type.startsWith(BAND_ACTIONS.getEventAccommodationReservations) ||
  action.type.startsWith(USER_ACTIONS.getUserAddresses) ||
  action.type.startsWith(USER_ACTIONS.getUserPhoneNumbers) ||
  action.type.startsWith(USER_ACTIONS.getUserDrivingPermits) ||
  action.type.startsWith(USER_ACTIONS.getUserEmergencyContacts) ||
  action.type.startsWith(USER_ACTIONS.getUserHomeBases) ||
  action.type.startsWith(USER_ACTIONS.getUserLoyaltyPrograms) ||
  action.type.startsWith(USER_ACTIONS.getUserPassports) ||
  action.type.startsWith(USER_ACTIONS.getUserSocialSecurityForms) ||
  action.type.startsWith(USER_ACTIONS.getUserTravelVisas) ||
  action.type.startsWith(USER_ACTIONS.getUserVehicles);

export const isPendingAction = (action: AnyAction): action is PendingAction => {
  return matchPrefix(action) && action.type.endsWith(`/${IActionStates.pending}`);
};
export const isRejectedAction = (action: AnyAction): action is RejectedAction => {
  return matchPrefix(action) && action.type.endsWith(`/${IActionStates.rejected}`);
};
export const isFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isFulfilledActionExceptionType(action)) return false;
  return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
};

// For create actions where you want to pass the created items id and/or item to the meta
export const isCreatedFulfilledAction = (
  action: AnyAction
): action is PayloadAction<{ data: any; id: string; createdItem?: IJSONAPIItem }, string, any> => {
  if (isCreatedFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For delete actions where you want to remove the item from the state
export const isDeletedFulfilledAction = (
  action: AnyAction
): action is PayloadAction<{ path: string; dataKey: IDataKeys }, string, any> => {
  if (isDeleteFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For edit actions where you want to replace the item in the state
export const isEditFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isEditFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For actions where you want to replace all the items under the key in the state instead of merging
export const isReplaceFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isReplaceFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For actions where you want to replace all the items under the key in the state instead of merging
export const isPaginatedFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isPaginatedFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

export const isBandDetailFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isBandDetailFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For actions where you want to add the linked contacts, files, places and images to the redux store
export const isInfoBlockDetailFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isInfoBlockDetailFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};
